// ./src/SessionPicker/SessionPicker.tsx
//
// ROADMAP A4 — choose one or several SESSIONS (the dates of a recurring event, or an agenda's sessions) without a list that
// grows with the schedule (owner, 2026-09-14). A strip of the DAYS that have sessions, then the TIMES on the chosen day as
// buttons — one day can hold several sessions (a 4 PM and a 7 PM show). A short schedule (≤ `flatThreshold`) skips the day
// step and shows every session as a button. Always in the EVENT's timezone.
//
// Presentational: the caller owns the selection and decides what is full, closed or locked. Used by the public RSVP form
// (multiple), the Buy tickets drawer and the box office (single).
import React, { useEffect, useMemo, useState } from "react";
import { ActionIcon, Box, Button, Group, Input, Pill, Popover, SimpleGrid, Stack, Text, UnstyledButton } from "@mantine/core";
import { DatePicker } from "@mantine/dates";
import { useElementSize } from "@mantine/hooks";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { eventTzAbbrev, resolveEventTz } from "@em/utils";

import { AppIcon } from "../AppIcon";

dayjs.extend(utc);
dayjs.extend(timezone);

/** The picker width from which full-label sessions sit two to a row (each still fits "Sat, Dec 26 · 10:00 AM"). */
const SESSION_TWO_COLUMNS_MIN = 520;
/** The narrowest column a time button ("10:00 AM" + a session name) is given. */
const SESSION_TIME_COLUMN_MIN = 140;

export interface SessionPickerOption {
  id: string;
  /** UTC. */
  startsAt?: string | null;
  endsAt?: string | null;
  isAllDay?: boolean | null;
  /** The session's own name, shown under its time. */
  name?: string | null;
  /** Places left; null = uncapped. */
  remaining?: number | null;
  /** Cannot be ADDED (full, closed). A selected one can still be removed. */
  disabled?: boolean;
  /** Cannot be changed at all (e.g. a past date the guest attended). */
  locked?: boolean;
  /** A short state shown on the button instead of the remaining count ("Sold out", "Attended"). */
  note?: string | null;
}

export interface SessionPickerLabels {
  timesIn: (tz: string) => string;
  previous: string;
  next: string;
  jumpToDate: string;
  selectedCount: (n: number) => string;
  allDay: string;
  left: (n: number) => string;
  timesCount: (n: number) => string;
  full: string;
  noSessions: string;
}

const DEFAULT_LABELS: SessionPickerLabels = {
  timesIn: (tz) => `Times in ${tz}`,
  previous: "Earlier dates",
  next: "Later dates",
  jumpToDate: "Jump to a date",
  selectedCount: (n) => (n === 1 ? "1 selected:" : `${n} selected:`),
  allDay: "All day",
  left: (n) => `${n} left`,
  timesCount: (n) => `${n} times`,
  full: "Full",
  noSessions: "No dates available",
};

export interface SessionPickerProps {
  options: SessionPickerOption[];
  timezone?: string | null;
  /** RSVP picks several; a sale picks one. */
  multiple: boolean;
  value: string[];
  onChange: (ids: string[]) => void;
  /** Days to mark with a dot (e.g. dates already in a cart). Defaults to the selection. */
  markedIds?: string[];
  /** At most this many sessions → one row of buttons with full labels, no day step (default 6). */
  flatThreshold?: number;
  /** Show "N left" at or below this many places (default 10). */
  lowStockThreshold?: number;
  label?: React.ReactNode;
  required?: boolean;
  error?: React.ReactNode;
  disabled?: boolean;
  labels?: Partial<SessionPickerLabels>;
}

interface Session extends SessionPickerOption {
  dayKey: string;
  time: string;
  fullLabel: string;
  ms: number;
}

export const SessionPicker: React.FC<SessionPickerProps> = ({
  options,
  timezone: tz,
  multiple,
  value,
  onChange,
  markedIds,
  flatThreshold = 6,
  lowStockThreshold = 10,
  label,
  required,
  error,
  disabled = false,
  labels,
}) => {
  const L = useMemo(() => ({ ...DEFAULT_LABELS, ...(labels ?? {}) }), [labels]);
  const zone = resolveEventTz(tz);
  const { ref: stripRef, width } = useElementSize();
  // The picker's OWN width (not the screen's): it sits in a drawer, a guest's card, a box-office panel.
  const { ref: boxRef, width: boxWidth } = useElementSize();
  const [jumpOpen, setJumpOpen] = useState(false);

  const sessions: Session[] = useMemo(
    () =>
      options
        .filter((o) => !!o.id && !!o.startsAt)
        .map((o) => {
          const z = dayjs.utc(o.startsAt as string).tz(zone);

          return {
            ...o,
            dayKey: z.format("YYYY-MM-DD"),
            time: o.isAllDay ? L.allDay : z.format("h:mm A"),
            fullLabel: z.format(o.isAllDay ? "ddd, MMM D" : "ddd, MMM D · h:mm A"),
            ms: z.valueOf(),
          };
        })
        .sort((a, b) => a.ms - b.ms),
    [options, zone, L.allDay]
  );

  const days = useMemo(() => {
    const byKey = new Map<string, Session[]>();
    for (const s of sessions) {
      byKey.set(s.dayKey, [...(byKey.get(s.dayKey) ?? []), s]);
    }

    return Array.from(byKey.entries()).map(([key, list]) => ({ key, date: dayjs(key), sessions: list }));
  }, [sessions]);

  const selected = useMemo(() => new Set(value), [value]);
  const marked = useMemo(() => new Set(markedIds ?? value), [markedIds, value]);
  const canPick = (s: Session): boolean => !disabled && !s.locked && (!s.disabled || selected.has(s.id));

  const toggle = (s: Session) => {
    if (!canPick(s)) {
      return;
    }

    if (!multiple) {
      onChange([s.id]);

      return;
    }

    onChange(selected.has(s.id) ? value.filter((id) => id !== s.id) : [...value, s.id]);
  };

  // The day on show: the first selected session's, else the first day with something to pick.
  const initialKey = useMemo(() => {
    const firstSelected = sessions.find((s) => selected.has(s.id));
    const firstOpen = sessions.find((s) => !s.disabled && !s.locked);

    return firstSelected?.dayKey ?? firstOpen?.dayKey ?? days[0]?.key ?? null;
    // Only when the schedule itself changes — not on every pick.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days]);
  const [activeKey, setActiveKey] = useState<string | null>(initialKey);
  useEffect(() => {
    if (!activeKey || !days.some((d) => d.key === activeKey)) {
      setActiveKey(initialKey);
    }
  }, [days, activeKey, initialKey]);
  // With one session to pick, the day on show follows a selection made from OUTSIDE too (Buy tickets' "Your order" →
  // Change opens that date) — never a strip on one day over another day's session.
  useEffect(() => {
    if (multiple) {
      return;
    }

    const picked = sessions.find((s) => selected.has(s.id));
    if (picked) {
      setActiveKey((key) => (key === picked.dayKey ? key : picked.dayKey));
    }
  }, [multiple, sessions, selected]);

  /**
   * The guest moved to another day (a tap on it, the strip's arrows, "Jump to a date"). ⚠ With ONE session to pick
   * (`multiple` false — Buy tickets, the box office) the selection follows the day on show: a day with a single time
   * selects it, a day with several clears the selection until a time is tapped. It used to stay on the PREVIOUS day's
   * session — the strip said Oct 30 while the tickets below, and every count picked there, were Oct 23's (ROADMAP #175,
   * owner, 2026-10-17: "user assumes that they just select one session"). Several sessions (RSVP) keep their picks.
   */
  const showDay = (key: string | null) => {
    setActiveKey(key);
    if (multiple || !key) {
      return;
    }

    const day = days.find((d) => d.key === key);
    if (!day || day.sessions.some((s) => selected.has(s.id))) {
      return;
    }

    const pickable = day.sessions.filter(canPick);
    const next = pickable.length === 1 && day.sessions.length === 1 ? [pickable[0].id] : [];

    if (next.length > 0 || value.length > 0) {
      onChange(next);
    }
  };

  const perPage = Math.max(3, Math.min(7, Math.floor((width || 360) / 64)));
  const activeIndex = Math.max(0, days.findIndex((d) => d.key === activeKey));
  const page = Math.floor(activeIndex / perPage);
  const pageCount = Math.max(1, Math.ceil(days.length / perPage));
  const visibleDays = days.slice(page * perPage, page * perPage + perPage);
  const activeDay = days[activeIndex] ?? null;
  const dayKeys = useMemo(() => new Set(days.map((d) => d.key)), [days]);
  // ⚠ The buttons sit on a GRID of equal columns (#136): in a wrapping row each took its own text's width, so on a phone
  //   every session was a different width, none of them the width of the form. Before the first measure: one column.
  const fullLabelCols = boxWidth >= SESSION_TWO_COLUMNS_MIN ? 2 : 1;
  const timeCols = Math.max(2, Math.min(4, Math.floor(boxWidth / SESSION_TIME_COLUMN_MIN) || 2));

  const sessionButton = (s: Session, flat: boolean) => {
    const isSelected = selected.has(s.id);
    const pickable = canPick(s);
    const meta = s.note ?? (s.disabled ? L.full : s.remaining != null && s.remaining <= lowStockThreshold ? L.left(s.remaining) : null);

    return (
      <Button
        key={s.id}
        variant={isSelected ? "filled" : "default"}
        onClick={() => toggle(s)}
        disabled={!pickable && !isSelected}
        aria-pressed={isSelected}
        size="md"
        fullWidth
        h="auto"
        py={8}
        px="sm"
        style={!pickable && isSelected ? { cursor: "default" } : undefined}
        styles={{ label: { flexDirection: "column", whiteSpace: "normal", lineHeight: 1.25, textAlign: "center" } }}
      >
        <span>{flat ? s.fullLabel : s.time}</span>
        {s.name ? (
          <Text span size="md" fw={400} lineClamp={2}>
            {s.name}
          </Text>
        ) : null}
        {meta ? (
          <Text span size="md" fw={400} style={{ opacity: 0.85 }}>
            {meta}
          </Text>
        ) : null}
      </Button>
    );
  };

  const selectedSessions = sessions.filter((s) => selected.has(s.id));
  const flat = sessions.length <= flatThreshold;

  return (
    <Input.Wrapper label={label} required={required} error={error}>
      <Stack ref={boxRef} gap="xs" mt={label ? 6 : 0}>
        {sessions.length === 0 ? (
          <Text size="md" c="dimmed">
            {L.noSessions}
          </Text>
        ) : flat ? (
          <SimpleGrid cols={fullLabelCols} spacing="xs" verticalSpacing="xs">
            {sessions.map((s) => sessionButton(s, true))}
          </SimpleGrid>
        ) : (
          <>
            <Group gap={6} wrap="nowrap" align="stretch">
              <ActionIcon
                variant="subtle"
                size="lg"
                h="auto"
                aria-label={L.previous}
                disabled={page === 0}
                onClick={() => showDay(days[(page - 1) * perPage]?.key ?? activeKey)}
              >
                <AppIcon iconName="ChevronLeft" size={18} />
              </ActionIcon>
              <Box ref={stripRef} style={{ flex: 1, minWidth: 0 }}>
                <SimpleGrid cols={perPage} spacing={6}>
                  {visibleDays.map((d) => {
                    const active = d.key === activeKey;
                    const open = d.sessions.filter((s) => !s.disabled && !s.locked);
                    const hasMark = d.sessions.some((s) => marked.has(s.id));
                    const hint = d.sessions.length > 1 ? L.timesCount(d.sessions.length) : open.length === 0 ? L.full : d.sessions[0].time;

                    return (
                      <UnstyledButton
                        key={d.key}
                        onClick={() => showDay(d.key)}
                        aria-pressed={active}
                        aria-label={d.date.format("dddd, MMMM D")}
                        style={{
                          borderRadius: "var(--mantine-radius-md)",
                          border: `1px solid ${active ? "var(--mantine-primary-color-filled)" : "var(--mantine-color-default-border)"}`,
                          background: active ? "var(--mantine-primary-color-light)" : undefined,
                          padding: "6px 2px",
                          textAlign: "center",
                          opacity: open.length === 0 && !hasMark ? 0.55 : 1,
                        }}
                      >
                        <Text size="xs" c="dimmed" tt="uppercase">
                          {d.date.format("ddd")}
                        </Text>
                        <Text fw={700} size="lg" lh={1.1}>
                          {d.date.format("D")}
                        </Text>
                        <Text size="xs" c="dimmed">
                          {d.date.format("MMM")}
                        </Text>
                        <Group gap={4} justify="center" wrap="nowrap" mih={16}>
                          {hasMark ? <Box w={6} h={6} style={{ borderRadius: 999, background: "var(--mantine-primary-color-filled)", flexShrink: 0 }} /> : null}
                          <Text size="xs" c="dimmed" truncate>
                            {hint}
                          </Text>
                        </Group>
                      </UnstyledButton>
                    );
                  })}
                </SimpleGrid>
              </Box>
              <ActionIcon
                variant="subtle"
                size="lg"
                h="auto"
                aria-label={L.next}
                disabled={page >= pageCount - 1}
                onClick={() => showDay(days[(page + 1) * perPage]?.key ?? activeKey)}
              >
                <AppIcon iconName="ChevronRight" size={18} />
              </ActionIcon>
            </Group>

            {activeDay ? (
              <Stack gap={6}>
                <Group justify="space-between" wrap="nowrap">
                  <Text size="md" fw={600}>
                    {activeDay.date.format("dddd, MMMM D")}
                  </Text>
                  {pageCount > 1 ? (
                    <Popover opened={jumpOpen} onChange={setJumpOpen} withinPortal={false} position="bottom-end" shadow="md">
                      <Popover.Target>
                        <Button variant="subtle" size="compact-sm" onClick={() => setJumpOpen((o) => !o)} leftSection={<AppIcon iconName="Calendar" size={14} />}>
                          {L.jumpToDate}
                        </Button>
                      </Popover.Target>
                      <Popover.Dropdown>
                        <DatePicker
                          defaultDate={activeDay.key}
                          minDate={days[0].key}
                          maxDate={days[days.length - 1].key}
                          excludeDate={(d) => !dayKeys.has(dayjs(d).format("YYYY-MM-DD"))}
                          value={activeDay.key}
                          onChange={(d) => {
                            if (d) {
                              showDay(dayjs(d).format("YYYY-MM-DD"));
                              setJumpOpen(false);
                            }
                          }}
                        />
                      </Popover.Dropdown>
                    </Popover>
                  ) : null}
                </Group>
                <SimpleGrid cols={timeCols} spacing="xs" verticalSpacing="xs">
                  {activeDay.sessions.map((s) => sessionButton(s, false))}
                </SimpleGrid>
              </Stack>
            ) : null}
          </>
        )}

        {multiple && selectedSessions.length > 0 ? (
          <Group gap={6}>
            <Text size="md" c="dimmed">
              {L.selectedCount(selectedSessions.length)}
            </Text>
            {selectedSessions.map((s) => (
              <Pill key={s.id} withRemoveButton={canPick(s)} onRemove={() => toggle(s)} onClick={() => setActiveKey(s.dayKey)} style={{ cursor: "pointer" }}>
                {s.fullLabel}
              </Pill>
            ))}
          </Group>
        ) : null}

        {sessions.length > 0 ? (
          <Text size="md" c="dimmed">
            {L.timesIn(eventTzAbbrev(zone, sessions[0].startsAt))}
          </Text>
        ) : null}
      </Stack>
    </Input.Wrapper>
  );
};
